interface ErrorObject {
  messages: string[]
  codes: string[]
}

export class PasswordError extends Error {
  private _errors: ErrorObject
  constructor(message: string, messages: string[] = [], codes: string[] = []) {
    super(message)

    this._errors = {
      messages,
      codes
    }
  }

  public get messages() {
    return this._errors.messages
  }

  public get codes() {
    return this._errors.codes
  }

  public get errors() {
    return this._errors
  }
}

const passwordRegexes = [
  {
    regex: /[A-Z]/,
    error: {
      message: 'Password must include at least one uppercase letter (A-Z)',
      messageShort: 'at least one uppercase letter (A-Z)',
      code: 'no-uppercase'
    }
  },
  {
    regex: /[a-z]/,
    error: {
      message: 'Password must include at least one lowercase letter (a-z)',
      messageShort: 'at least one lowercase letter (a-z)',
      code: 'no-lowercase'
    }
  },
  {
    regex: /[\d]|['#!$%\^&\*{}\[\]|=\+\-_()\@]/,
    error: {
      message: 'Password must include at least one number or non-alphanumeric character (! @ # $ % ^ & * ( ) _ + - = [ ] { } | \')',
      code: 'no-number-or-alphanumeric',
      messageShort: 'at least one number or alphanumeric character'
    }
  },
  {
    regex: /^[A-z'#!$%\^&\*{}\[\]|=\+\-_()\@\d]{8,32}$/,
    error: {
      message: 'Password must include only valid characters between 8 and 32 in length',
      messageShort: 'only 8 to 32 valid characters',
      code: 'no-valid-characters-or-length'
    }
  }
]

export const isPasswordValid = (password: string) => {
  const messages: string[] = []
  const codes: string[] = []
  for (const item of passwordRegexes) {
    if (!item.regex.test(password)) {
      messages.push(item.error.message)
      codes.push(item.error.code)
    }
  }

  if (messages.length) {
    throw new PasswordError('Invalid password.', messages, codes)
  }

  return true
}

export type PasswordRequirementState = { requirement: string, isMet: boolean, requirementShort: string, translationId: string }
/**
 * Returns an array of password requirements and whether each is met.
 * @param password The password to validate
 * @returns Array of { requirement, isMet }
 */
export function getPasswordValidationState(password: string): Array<PasswordRequirementState> {
  return passwordRegexes.map(item => ({
    requirement: item.error.message,
    requirementShort: item.error.messageShort,
    translationId: item.error.code,
    isMet: item.regex.test(password)
  }))
}
